import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { apiErrorMessage } from "@/api/client";
import type { PaginationMeta } from "./types";

const EXTRA_KEYS = [
  "creator_profile_id",
  "creator_program_id",
  "creator_campaign_id",
  "user_id",
  "current_tier",
  "assigned_reviewer_user_id",
] as const;

export function useCreatorQueue<T>(
  loadPage: (params: {
    cursor?: string;
    q: string;
    status: string;
    sort: string;
    extra: Record<string, string>;
  }) => Promise<{ items: T[]; pagination: PaginationMeta }>,
) {
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get("q") ?? "";
  const status = searchParams.get("status") ?? "";
  const sort = searchParams.get("sort") ?? "";
  const cursor = searchParams.get("cursor") ?? undefined;
  const extra: Record<string, string> = {};
  for (const key of EXTRA_KEYS) {
    const value = searchParams.get(key);
    if (value) extra[key] = value;
  }

  const [rows, setRows] = useState<T[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchDraft, setSearchDraft] = useState(q);

  const extraKey = JSON.stringify(extra);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await loadPage({
        ...(cursor ? { cursor } : {}),
        q,
        status,
        sort,
        extra: JSON.parse(extraKey) as Record<string, string>,
      });
      setRows(result.items);
      setNextCursor(result.pagination.next_cursor);
    } catch (err) {
      setError(apiErrorMessage(err));
      setRows([]);
      setNextCursor(null);
    } finally {
      setLoading(false);
    }
  }, [loadPage, cursor, q, status, sort, extraKey]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    setSearchDraft(q);
  }, [q]);

  function updateParams(patch: Record<string, string | undefined>) {
    const next = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(patch)) {
      if (value === undefined || value === "") next.delete(key);
      else next.set(key, value);
    }
    setSearchParams(next);
  }

  const page = cursorStack.length + 1;

  return {
    q,
    status,
    sort,
    extra,
    rows,
    loading,
    error,
    searchDraft,
    setSearchDraft,
    nextCursor,
    page,
    load,
    applySearch() {
      setCursorStack([]);
      updateParams({ q: searchDraft.trim() || undefined, cursor: undefined });
    },
    setStatus(value: string) {
      setCursorStack([]);
      updateParams({ status: value || undefined, cursor: undefined });
    },
    setSort(value: string) {
      setCursorStack([]);
      updateParams({ sort: value, cursor: undefined });
    },
    goNext() {
      if (!nextCursor) return;
      setCursorStack((stack) => [...stack, cursor ?? ""]);
      updateParams({ cursor: nextCursor });
    },
    goPrevious() {
      if (cursorStack.length === 0) return;
      const stack = [...cursorStack];
      const previous = stack.pop();
      setCursorStack(stack);
      updateParams({
        cursor: previous && previous.length > 0 ? previous : undefined,
      });
    },
  };
}
